import {getRuleAccessBitIndex, ruleStructure} from '../../../shared-utilities/models-old/utils-old/rule-structure';
import {IGroupAccess} from '../models/group-access';
import {RuleHumanIdEnum, RuleHumanID} from '../models/rule-info.enum';

/** @deprecated Legacy — not assignable via Customise Permissions UI */
export const LEGACY_MODAL_RULE_IDS: readonly RuleHumanID[] = [
  RuleHumanIdEnum['d.i'],
  RuleHumanIdEnum['d.ii'],
];

export interface ICustomPermNode {
  key: RuleHumanID;
  label: string;
  children?: ICustomPermNode[];
  /** Visual indent without parent/child selection behaviour */
  indent?: boolean;
  tooltip?: string;
}

/** Permissions that do not require View / Search Stock when selected */
export const VIEW_SEARCH_EXEMPT_RULE_IDS: readonly RuleHumanID[] = [
  RuleHumanIdEnum['b.'],
  RuleHumanIdEnum['e.'],
];

export interface ICustomPermSection {
  category: string | null;
  nodes: ICustomPermNode[];
}

export const CUSTOM_PERM_SECTIONS: ICustomPermSection[] = [
  {
    category: null,
    nodes: [{key: RuleHumanIdEnum['a.'], label: 'Edit Staff Access'}],
  },
  {
    category: 'General',
    nodes: [
      {
        key: RuleHumanIdEnum['e.'],
        label: 'View Dashboard Reporting',
        tooltip: 'View store analytics and access all store data.',
      },
      {key: RuleHumanIdEnum['d.vi'], label: 'View & Edit Settings'},
    ],
  },
  {
    category: 'Stock',
    nodes: [
      {
        key: RuleHumanIdEnum['b.'],
        label: 'View / Search Stock',
        tooltip:
          'Can be enabled on its own for view-only access. Required when any other permission is selected (except View Dashboard Reporting) and cannot be turned off until those are removed.',
      },
      {key: RuleHumanIdEnum['b.i'], label: 'Access Stock Edit Interface', indent: true},
      {key: RuleHumanIdEnum['b.ii'], label: 'Submit Stock Edits to IQ', indent: true},
      {
        key: RuleHumanIdEnum['c.'],
        label: 'View NGP Report',
        tooltip: 'Access the Nett Gross Profit report for this store.',
      },
      {
        key: RuleHumanIdEnum['f.'],
        label: 'View Shelf Talkers',
        tooltip:
          'Create and print shelf talker labels. Also allows updating stock item tags (`_tags` only) — not general stock fields.',
      },
    ],
  },
  {
    category: 'Ordering',
    nodes: [
      {
        key: RuleHumanIdEnum['d.'],
        label: 'Access Auto Ordering Tool',
        tooltip: 'Access the auto ordering tool to review and manage supplier orders.',
        children: [
          {key: RuleHumanIdEnum['d.iii'], label: 'Submit Auto Orders'},
          {key: RuleHumanIdEnum['d.iv'], label: 'Edit Schedule'},
          {key: RuleHumanIdEnum['d.v'], label: 'Run Unscheduled Orders'},
        ],
      },
    ],
  },
  {
    category: 'Stock Take',
    nodes: [
      {
        key: RuleHumanIdEnum['j.'],
        label: 'Manage Stock Takes',
        tooltip: 'Create, edit, delete, and process stock takes and schedules.',
      },
    ],
  },
  {
    category: 'Mobile',
    nodes: [
      {
        key: RuleHumanIdEnum['g.'],
        label: 'Barcode Scanner',
        tooltip:
          'Access the mobile barcode scanner. Also allows updating stock item tags (`_tags` only) — not general stock fields.',
      },
      {
        key: RuleHumanIdEnum['b.ii'],
        label: 'Submit Stock Edits to IQ',
        indent: true,
        tooltip: 'Linked to the same permission under Stock — submit barcode scanner edits to IQ.',
      },
      {
        key: RuleHumanIdEnum['i.'],
        label: 'Stock Take Counter',
        tooltip: 'Mobile-only stock take access. Count products only — cannot process or delete.',
      },
    ],
  },
];

export const CUSTOM_PERM_TREE: ICustomPermNode[] = CUSTOM_PERM_SECTIONS.flatMap((section) => section.nodes);

/**
 * Flattens permission nodes including nested children for assignable-rule lookups.
 */
export function flattenPermNodes(nodes: ICustomPermNode[]): ICustomPermNode[] {
  return nodes.flatMap((node) => [node, ...flattenPermNodes(node.children ?? [])]);
}

const MODAL_ASSIGNABLE_RULE_IDS = new Set<RuleHumanID>(
  flattenPermNodes(CUSTOM_PERM_SECTIONS.flatMap((section) => section.nodes)).map((node) => node.key),
);

/**
 * Returns modal permissions that require View / Search Stock when selected.
 */
export function getRulesRequiringViewSearch(): RuleHumanID[] {
  return Array.from(MODAL_ASSIGNABLE_RULE_IDS).filter(
    (ruleId) => !VIEW_SEARCH_EXEMPT_RULE_IDS.includes(ruleId),
  );
}

/**
 * Returns true when any selected permission requires View / Search Stock.
 */
export function hasViewSearchRequirement(selected: Set<RuleHumanID>): boolean {
  return getRulesRequiringViewSearch().some((ruleId) => selected.has(ruleId));
}

/**
 * Returns all descendant rule IDs for a permission tree node.
 */
export function allChildRuleIds(node: ICustomPermNode): RuleHumanID[] {
  if (!node.children?.length) {
    return [];
  }
  return node.children.flatMap((child) => [child.key, ...allChildRuleIds(child)]);
}

/**
 * Builds a store access bitmask from selected rule human IDs.
 */
export function accessCodeFromRuleHumanIds(ids: RuleHumanID[]): number {
  let accessCode = 0;
  for (const humanId of ids) {
    const rule = ruleStructure.dictionary[humanId];
    if (!rule) {
      continue;
    }
    const bitIndex = getRuleAccessBitIndex(rule);
    accessCode |= 1 << bitIndex;
  }
  return accessCode;
}

/**
 * Decodes a store access bitmask into rule human IDs shown in the customise modal.
 */
export function ruleHumanIdsFromAccessCode(
  accessCode: number,
  options?: { excludeLegacy?: boolean },
): RuleHumanID[] {
  const excludeLegacy = options?.excludeLegacy ?? true;
  const result: RuleHumanID[] = [];

  for (const humanId of MODAL_ASSIGNABLE_RULE_IDS) {
    if (excludeLegacy && LEGACY_MODAL_RULE_IDS.includes(humanId)) {
      continue;
    }
    const rule = ruleStructure.dictionary[humanId];
    if (!rule) {
      continue;
    }
    const bitIndex = getRuleAccessBitIndex(rule);
    if (Math.floor((accessCode / Math.pow(2, bitIndex)) % 2) === 1) {
      result.push(humanId);
    }
  }

  return result;
}

/**
 * Derives default modal rule IDs for a preset role from its Firestore access code.
 */
export function roleDefaultRuleIds(groupName: string, groupAccess: IGroupAccess): RuleHumanID[] {
  const accessCode = groupAccess[groupName]?.accessCode;
  if (accessCode === undefined) {
    return [];
  }
  return ruleHumanIdsFromAccessCode(accessCode);
}

/**
 * Returns true when customised rule selections differ from the selected role defaults.
 */
export function isCustomised(
  groupName: string,
  customIds: RuleHumanID[] | undefined,
  groupAccess: IGroupAccess,
): boolean {
  if (!customIds?.length) {
    return false;
  }
  const defaults = roleDefaultRuleIds(groupName, groupAccess);
  if (customIds.length !== defaults.length) {
    return true;
  }
  const defaultSet = new Set(defaults);
  return customIds.some((id) => !defaultSet.has(id));
}

/**
 * Finds the preset group whose modal rule set best matches a non-preset access code.
 */
export function findBestMatchingGroupName(accessCode: number, groupAccess: IGroupAccess): string | null {
  const decoded = new Set(ruleHumanIdsFromAccessCode(accessCode));
  let bestGroup: string | null = null;
  let bestScore = -1;

  for (const groupName of Object.keys(groupAccess)) {
    if (groupName === 'No Access' || groupName === 'Custom') {
      continue;
    }
    const defaults = roleDefaultRuleIds(groupName, groupAccess);
    const overlap = defaults.filter((id) => decoded.has(id)).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      bestGroup = groupName;
    }
  }

  return bestGroup;
}

/**
 * Returns whether a parent node is partially selected.
 */
export function isIndeterminate(node: ICustomPermNode, selected: Set<RuleHumanID>): boolean {
  const childKeys = allChildRuleIds(node);
  if (!childKeys.length) {
    return false;
  }
  const selectedCount = childKeys.filter((key) => selected.has(key)).length;
  return selectedCount > 0 && selectedCount < childKeys.length;
}

/**
 * Returns true when View / Search Stock cannot be unchecked because other permissions are selected.
 */
export function isViewSearchLocked(selected: Set<RuleHumanID>): boolean {
  return selected.has(RuleHumanIdEnum['b.']) && hasViewSearchRequirement(selected);
}

/**
 * Returns true when View / Search Stock may be unchecked.
 */
export function canUncheckViewSearch(selected: Set<RuleHumanID>): boolean {
  return !hasViewSearchRequirement(selected);
}

/**
 * Ensures View / Search Stock is selected when any dependent permission is enabled.
 */
export function syncStockViewSearchRequirement(selected: Set<RuleHumanID>): Set<RuleHumanID> {
  const next = new Set(selected);
  if (hasViewSearchRequirement(next)) {
    next.add(RuleHumanIdEnum['b.']);
  }
  return next;
}

/**
 * Toggles a permission node and its descendants; keeps parent/child selection in sync.
 */
export function toggleRuleSelection(
  node: ICustomPermNode,
  checked: boolean,
  selected: Set<RuleHumanID>,
): Set<RuleHumanID> {
  const next = new Set(selected);
  const childKeys = allChildRuleIds(node);

  if (node.key === RuleHumanIdEnum['a.']) {
    return next;
  }

  if (node.key === RuleHumanIdEnum['b.'] && !checked && !canUncheckViewSearch(next)) {
    return selected;
  }

  if (childKeys.length > 0) {
    if (checked) {
      next.add(node.key);
      childKeys.forEach((key) => next.add(key));
    } else {
      next.delete(node.key);
      childKeys.forEach((key) => next.delete(key));
    }
    return syncStockViewSearchRequirement(syncParentSelections(next));
  }

  if (checked) {
    next.add(node.key);
  } else {
    next.delete(node.key);
  }

  return syncStockViewSearchRequirement(syncParentSelections(next));
}

/**
 * Ensures parent nodes reflect child selections: parents are selected when any child is,
 * and cleared only when no children remain selected.
 */
export function syncParentSelections(selected: Set<RuleHumanID>): Set<RuleHumanID> {
  const next = new Set(selected);

  for (const parent of CUSTOM_PERM_TREE) {
    if (!parent.children?.length) {
      continue;
    }
    const descendantKeys = allChildRuleIds(parent);
    const anyChildSelected = descendantKeys.some((key) => next.has(key));
    if (anyChildSelected) {
      next.add(parent.key);
    } else {
      next.delete(parent.key);
    }
  }

  return next;
}

/**
 * Preset role names suitable for the role dropdown (excludes No Access and Custom).
 */
export function getPresetRoleNames(accessGroupNames: string[]): string[] {
  return accessGroupNames.filter((name) => name !== 'No Access' && name !== 'Custom');
}
